import { ReactElement, StrictMode, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
	createRoutesFromChildren,
	matchRoutes,
	RouterProvider,
	useLocation,
	useNavigationType,
} from 'react-router-dom';
import { YMInitializer } from 'react-yandex-metrika';

import * as Sentry from '@sentry/react';
import { isTMA } from '@telegram-apps/bridge';
import { init } from '@telegram-apps/sdk-react';

import { App, unstableSetRender } from 'antd';
import ru from 'antd/locale/ru_RU';

import { store } from 'app/appStore';
import { ymIds, ymOptions } from 'shared/consts';

import '@ant-design/v5-patch-for-react-19';
import 'shared/styles/index.scss';

import { sentryEnvironment } from '../../sentry-environment';

import appRouter from './appRouter';
import { ConfigProvider } from './providers/ConfigProvider';

if (isTMA()) {
	init();
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_ENVIRONMENT;

!!sentryDsn &&
	Sentry.init({
		dsn: sentryDsn,
		integrations: [
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			Sentry.replayIntegration(),
		],
		denyUrls: [
			// Chrome extensions
			/extensions\//i,
			/extension\//i,
			/^chrome:\/\//i,
			// Facebook flakiness
			/graph\.facebook\.com/i,
		],
		release: sentryEnvironment.release,
		tracesSampleRate: 1.0,
		environment,
	});

/**
 * Creates the main React root and targets the HTML element with the id="root".
 * Renders the React application within the created root.
 */
createRoot(document.getElementById('root')!).render(
	<StrictMode>
		<Provider store={store}>
			<ConfigProvider locale={{ ...ru, Text: { ...ru.Text, expand: 'Показать полностью', collapse: 'Скрыть' } }}>
				<App>
					<RouterProvider router={appRouter} />
					<YMInitializer accounts={ymIds} options={ymOptions} version="2" />
				</App>
			</ConfigProvider>
		</Provider>
	</StrictMode>,
);

/**
 * Maintain a mapping between portal container elements and their React roots.
 * This prevents calling `createRoot` more than once on the same container.
 */
const portalRoots = new WeakMap<Element | DocumentFragment, Root>();

/**
 * Overrides the default patch of rendering behavior for Ant Design portal components.
 * IMPORTANT: Will be removed after adding React 19 support out-of-the-box.
 *
 * @param node – The React element to render in the portal.
 * @param container – The DOM container where the element should be rendered.
 * @returns A cleanup function to unmount the portal.
 */
unstableSetRender((node: ReactElement, container: Element | DocumentFragment) => {
	// Try to retrieve an existing root for this container
	let portalRoot = portalRoots.get(container);

	// If no root exists yet, create one and store it in the map
	if (!portalRoot) {
		portalRoot = createRoot(container);
		portalRoots.set(container, portalRoot);
	}

	// Render or update the portal with the provided React element
	portalRoot.render(node);

	// Return a cleanup function that unmounts the portal and removes it from the map
	return async () => {
		await new Promise((resolve) => setTimeout(resolve, 0));
		portalRoot?.unmount();
		portalRoots.delete(container);
	};
});
